import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageSquare, Paperclip, X, FileText, AtSign, Upload, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import RichEditor from './RichEditor';
import CommentItem from './CommentItem';
import useCardPanelStore from '../../../store/cardPanelStore';
import useBoardStore from '../../../store/boardStore';
import useBoardCan, { useHasServerAccess } from '../../../hooks/useBoardCan';
import { newKey, loadDraft, saveDraft, saveDraftDebounced, clearDraft, isEmptyHtml } from '../../../utils/commentDrafts';

function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function makeAttachment(file) {
  return {
    localId: crypto.randomUUID(),
    file,
    name: file.name,
    size: file.size,
    mimeType: file.type,
    previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    status: 'uploading',
    progress: 0,
    assetId: null,
    error: null,
  };
}

export default function UpdatesTab({ cardId }) {
  const { comments, loadingComments, addComment, uploadCommentAsset, setEditorDirty, unregisterEditor, isDiscarding } = useCardPanelStore();
  const users = useBoardStore((s) => s.users);
  // Backend (boardRules.js): comentar exige so 'read' (rank 1 — viewer
  // comenta); anexar exige 'edit_content' (board.file.upload, rank 2).
  // Nenhum dos dois tinha gate no comportamento atual — fallback preserva
  // isso (sempre liberado); so passa a valer quando o backend novo manda
  // my_access de fato.
  const can = useBoardCan();
  const hasServerAccess = useHasServerAccess();
  const canComment = hasServerAccess ? can.read : true;
  const canAttach = hasServerAccess ? can.edit_content : true;
  const [text, setText] = useState(() => loadDraft(newKey(cardId)) ?? '');
  const [posting, setPosting] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);
  const uploadPromisesRef = useRef(new Map());

  // cardId atual num ref para o flush no unmount sempre usar a key certa
  const cardIdRef = useRef(cardId);
  cardIdRef.current = cardId;

  // Recarrega draft SÓ quando o card realmente muda (o componente pode não
  // desmontar ao trocar de card). No primeiro mount NÃO mexe no editor: o
  // lazy init de `text` + `content=value` do RichEditor já restauram o draft.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const next = loadDraft(newKey(cardId)) ?? '';
    setText(next);
    // Troca de card: sincroniza o editor com o draft do novo card.
    editorRef.current?.setContent ? editorRef.current.setContent(next) : editorRef.current?.clear();
  }, [cardId]);

  // Reporta ao store se há texto não enviado neste editor (para o popup de
  // descarte). Desregistra ao trocar de card / desmontar.
  useEffect(() => {
    const key = newKey(cardId);
    setEditorDirty(key, !isEmptyHtml(text));
    return () => unregisterEditor(key);
  }, [cardId, text, setEditorDirty, unregisterEditor]);

  const hasInlineImage = text?.includes('<img');
  const isEmpty = !hasInlineImage && (!text || text === '<p></p>' || text.replace(/<[^>]+>/g, '').trim() === '');
  const canPost = !isEmpty || attachments.length > 0;
  const anyUploading = attachments.some((a) => a.status === 'uploading');

  const updateAttachment = useCallback((localId, patch) => {
    setAttachments((prev) => prev.map((a) => a.localId === localId ? { ...a, ...patch } : a));
  }, []);

  const startUpload = useCallback((attachment) => {
    const { localId, file } = attachment;
    const promise = uploadCommentAsset(cardId, file, (p) => {
      updateAttachment(localId, { progress: p });
    }).then((asset) => {
      updateAttachment(localId, { status: 'done', progress: 100, assetId: asset.id });
    }).catch((err) => {
      const msg = err?.message || 'Erro no upload';
      updateAttachment(localId, { status: 'error', error: msg });
    });
    uploadPromisesRef.current.set(localId, promise);
  }, [cardId, uploadCommentAsset, updateAttachment]);

  const addAttachments = useCallback((files) => {
    const newAttachments = files.map(makeAttachment);
    setAttachments((prev) => [...prev, ...newAttachments]);
    newAttachments.forEach((att) => startUpload(att));
  }, [startUpload]);

  const removeAttachment = useCallback((localId) => {
    setAttachments((prev) => {
      const att = prev.find((a) => a.localId === localId);
      if (att?.previewUrl) URL.revokeObjectURL(att.previewUrl);
      uploadPromisesRef.current.delete(localId);
      return prev.filter((a) => a.localId !== localId);
    });
  }, []);

  const retryUpload = useCallback((localId) => {
    setAttachments((prev) => {
      const att = prev.find((a) => a.localId === localId);
      if (!att) return prev;
      const updated = prev.map((a) => a.localId === localId ? { ...a, status: 'uploading', progress: 0, error: null } : a);
      // kick off upload after state update
      setTimeout(() => startUpload(att), 0);
      return updated;
    });
  }, [startUpload]);

  const handleFilesPick = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) addAttachments(picked);
    e.target.value = '';
  };

  const handlePost = async () => {
    if (!canPost) return;
    const textToSend = isEmpty ? '' : text;

    // Aguardar uploads pendentes
    if (uploadPromisesRef.current.size > 0) {
      setPosting(true);
      await Promise.allSettled([...uploadPromisesRef.current.values()]);
    }

    // Ler estado atual via functional update para garantir snapshot mais recente
    let currentAttachments;
    setAttachments((prev) => { currentAttachments = prev; return prev; });
    // Yield para garantir que o estado foi lido
    await new Promise((r) => setTimeout(r, 0));

    // Re-ler do ref atualizado
    const assetIds = (currentAttachments || attachments)
      .filter((a) => a.status === 'done' && a.assetId != null)
      .map((a) => a.assetId);

    // Revogar URLs antes de limpar
    (currentAttachments || attachments).forEach((a) => {
      if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
    });

    clearDraft(newKey(cardId));
    setText('');
    setAttachments([]);
    editorRef.current?.clear();
    uploadPromisesRef.current.clear();

    setPosting(true);
    try {
      await addComment(cardId, textToSend, null, [], assetIds);
    } finally {
      setPosting(false);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    dragCounterRef.current++;
    if (Array.from(e.dataTransfer.types).includes('Files')) setIsDragOver(true);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const handleDragLeave = () => {
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) setIsDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    const allFiles = Array.from(e.dataTransfer.files);
    if (allFiles.length > 0) addAttachments(allFiles);
  };

  const handlePaste = (e) => {
    if (e.target.closest?.('.ProseMirror')) return;
    const files = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith('image/'));
    if (files.length > 0) {
      e.preventDefault();
      addAttachments(files);
    }
  };

  const roots = comments.filter((c) => !c.parent_id).reverse();
  const repliesByParent = comments.reduce((acc, c) => {
    if (c.parent_id) {
      if (!acc[c.parent_id]) acc[c.parent_id] = [];
      acc[c.parent_id].push(c);
    }
    return acc;
  }, {});

  const totalImages = loadingComments ? 0 : comments.reduce(
    (sum, c) => sum + (c.assets || []).filter((a) => a.mime_type?.startsWith('image/')).length, 0
  );
  const [loadedImages, setLoadedImages] = useState(0);
  const onImageReady = useCallback(() => setLoadedImages((n) => n + 1), []);
  useEffect(() => { if (loadingComments) setLoadedImages(0); }, [loadingComments]);
  const ready = !loadingComments && loadedImages >= totalImages;

  const btnLabel = anyUploading ? 'Aguardando upload...' : posting ? 'Enviando...' : 'Atualizar';

  return (
    <div className="flex flex-col h-full" onPaste={canAttach ? handlePaste : undefined}>
      {canComment && (
      <div
        className="p-5 border-b border-border/50 relative flex flex-col"
        onDragEnter={canAttach ? handleDragEnter : undefined}
        onDragOver={canAttach ? handleDragOver : undefined}
        onDragLeave={canAttach ? handleDragLeave : undefined}
        onDrop={canAttach ? handleDrop : undefined}
      >
        {isDragOver && (
          <div className="absolute inset-0 z-10 m-1.5 rounded-lg border-2 border-dashed border-accent bg-accent/10 flex flex-col items-center justify-center gap-2 pointer-events-none">
            <Upload className="w-8 h-8 text-accent" />
            <span className="text-sm font-medium text-accent">Solte para anexar</span>
          </div>
        )}
        <div className="max-h-72 overflow-y-auto">
          <RichEditor
            ref={editorRef}
            value={text}
            onChange={(html) => { setText(html); saveDraftDebounced(newKey(cardId), html); }}
            onFlush={(html) => { if (isDiscarding()) return; saveDraft(newKey(cardIdRef.current), html); }}
            users={users}
            onAttachFiles={canAttach ? addAttachments : undefined}
          />
          {attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
            {attachments.map((att) => {
              const isImg = att.mimeType.startsWith('image/');
              return (
                <div
                  key={att.localId}
                  className={[
                    'relative group flex flex-col gap-1 px-2 py-1 bg-main border rounded text-xs',
                    att.status === 'error' ? 'border-danger' : 'border-border',
                  ].join(' ')}
                >
                  <div className="flex items-center gap-2">
                    {/* Thumbnail ou ícone */}
                    <div className="relative shrink-0">
                      {isImg && att.previewUrl ? (
                        <img src={att.previewUrl} alt={att.name} className="w-8 h-8 object-cover rounded" />
                      ) : (
                        <FileText className="w-4 h-4 text-text-secondary" />
                      )}
                      {/* Overlay de spinner durante upload sobre a thumbnail */}
                      {att.status === 'uploading' && isImg && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded">
                          <Loader2 className="w-3 h-3 text-white animate-spin" />
                        </div>
                      )}
                    </div>

                    {/* Spinner inline para não-imagens */}
                    {att.status === 'uploading' && !isImg && (
                      <Loader2 className="w-3 h-3 text-accent animate-spin shrink-0" />
                    )}

                    {/* Ícone de erro */}
                    {att.status === 'error' && (
                      <AlertCircle className="w-3 h-3 text-danger shrink-0" title={att.error || 'Erro no upload'} />
                    )}

                    {/* Ícone de sucesso */}
                    {att.status === 'done' && (
                      <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />
                    )}

                    <span className="truncate max-w-[160px] text-text-primary">{att.name}</span>
                    <span className="text-[10px] text-text-secondary">{fmtSize(att.size)}</span>

                    {/* Retry em caso de erro */}
                    {att.status === 'error' && (
                      <button
                        onClick={() => retryUpload(att.localId)}
                        className="text-[10px] text-accent hover:underline shrink-0"
                        title="Tentar novamente"
                      >
                        Retry
                      </button>
                    )}

                    <button
                      onClick={() => removeAttachment(att.localId)}
                      className="p-0.5 rounded hover:bg-danger/20 text-text-secondary hover:text-danger shrink-0"
                      title="Remover"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>

                  {/* Barra de progresso */}
                  {att.status === 'uploading' && (
                    <div className="h-0.5 bg-white/10 rounded overflow-hidden w-full">
                      <div
                        className="h-full bg-accent transition-all duration-150"
                        style={{ width: `${att.progress}%` }}
                      />
                    </div>
                  )}

                  {/* Tooltip de erro */}
                  {att.status === 'error' && att.error && (
                    <span className="text-[10px] text-danger truncate max-w-[200px]">{att.error}</span>
                  )}
                </div>
              );
            })}
            </div>
          )}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center gap-1">
            {canAttach && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-1.5 rounded text-text-secondary hover:text-accent hover:bg-white/5 transition-colors"
                title="Anexar arquivo"
              >
                <Paperclip className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => editorRef.current?.mention()}
              className="p-1.5 rounded text-text-secondary hover:text-accent hover:bg-white/5 transition-colors"
              title="Mencionar usuário"
            >
              <AtSign className="w-4 h-4" />
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFilesPick}
            className="hidden"
          />
          <button
            onClick={handlePost}
            disabled={posting || !canPost}
            className="px-4 py-1.5 text-sm font-medium bg-accent text-white rounded-lg hover:bg-accent/80 transition-colors disabled:opacity-40"
          >
            {btnLabel}
          </button>
        </div>
      </div>
      )}
      <div className="flex-1 overflow-y-auto p-5">
        {/* Skeleton — visível até tudo carregar */}
        {!ready && (
          <div className="space-y-6 py-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-9 h-9 rounded-full bg-white/10 shrink-0" />
                  <div className="flex-1 space-y-2 pt-1">
                    <div className="h-3 bg-white/10 rounded w-32" />
                    <div className="h-2.5 bg-white/10 rounded w-48" />
                  </div>
                </div>
                <div className="space-y-2 pl-12">
                  <div className="h-2.5 bg-white/10 rounded" style={{ width: `${[75, 55, 85][i]}%` }} />
                  <div className="h-2.5 bg-white/10 rounded" style={{ width: `${[90, 70, 60][i]}%` }} />
                  <div className="h-2.5 bg-white/10 rounded" style={{ width: `${[45, 80, 40][i]}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Conteúdo: sempre no DOM para AuthedImage montar e carregar imagens */}
        <div className={ready ? undefined : 'invisible h-0 overflow-hidden'}>
          {roots.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-text-secondary/30 mx-auto mb-3" />
              <div className="text-sm font-semibold text-text-primary mb-1">Nenhuma atualização ainda</div>
              <div className="text-xs text-text-secondary">Compartilhe o progresso, mencione um colega ou carregue um arquivo.</div>
            </div>
          ) : (
            <div className="group/c space-y-0">
              {roots.map((c) => (
                <div key={c.id} className="group/c">
                  <CommentItem
                    comment={c}
                    replies={repliesByParent[c.id] || []}
                    cardId={cardId}
                    users={users}
                    onImageReady={onImageReady}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
